import React from 'react';
import PageTemplatePlanoAnual from '../templates/PageTemplatePlanoAnual';
import StyledGlobal from '../../../PlanoAnual2025.styled';
import GridColumns from 'pages/ebooks/components/templates/GridColumns';
import { fetchStorageFile } from 'hooks/StoreFiles';
import { Divider, Image } from 'antd';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import Colors from 'enums/Colors';
import ResponsiveGrid from 'pages/ebooks/components/templates/ResponsiveGrid';
import { IsCustomMaxWidth } from 'core/util/WindowSize';

const Styled = {
    Item: {
        Container: styled.div<{ isColumn?: boolean }>`
            display: flex;
            align-items: center;
            flex-direction: ${props => (props.isColumn ? 'column' : 'row')};
            gap: 20px;
            background-color: #efedec;
            border-radius: 20px;

            width: ${props => (props.isColumn ? '200px' : 'auto')};
        `,

        ContentImg: styled.div`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100px;
            height: 100px;
        `,

        Content: styled.div<{ isColumn?: boolean }>`
            display: flex;
            flex-direction: column;
            gap: 10px;
            background-color: #f8f8f8;
            height: 100%;
            width: 100%;
            padding: 20px;
            border-radius: 20px;
            align-items: ${props => (props.isColumn ? 'center' : '')};
            justify-content: ${props => (props.isColumn ? 'center' : '')};
        `,

        Title: styled.h1`
            color: ${Colors.Blue};
            font-size: 18px;
            font-weight: bold;
            text-transform: uppercase;
        `,

        SubTitle: styled.h2`
            font-size: 20px;
            font-weight: bold;
            color: ${Colors.Yellow};
        `,
    },
};

const CardItem = (props: {
    imgIcon: string;
    title?: string;
    subTitle?: string;
    description: string;
    isColumn?: boolean;
    imgStyle?: React.CSSProperties;
}) => {
    return (
        <Styled.Item.Container isColumn={props.isColumn}>
            <Styled.Item.ContentImg>
                <img style={{ height: '70px', width: '70px', objectFit: 'contain', ...props.imgStyle }} src={props.imgIcon} alt="" />
            </Styled.Item.ContentImg>
            <Styled.Item.Content isColumn={props.isColumn}>
                {props.title && <Styled.Item.Title>{props.title}</Styled.Item.Title>}
                {props.subTitle && <Styled.Item.SubTitle>{props.subTitle}</Styled.Item.SubTitle>}
                <p style={{ textAlign: props.isColumn ? 'center' : 'justify', fontSize: '18px', lineHeight: '1.2' }}>{props.description}</p>
            </Styled.Item.Content>
        </Styled.Item.Container>
    );
};

const SessionGeralPlanoAnual = () => {
    return (
        <>
            <PageTemplatePlanoAnual numberPage={5}>
                <StyledGlobal.Title blue fontSize="32px" maxFontSize="24px">
                    1. APRESENTAÇÃO GERAL DO PLANO ANUAL
                </StyledGlobal.Title>

                <ResponsiveGrid
                    templates={{
                        desktop: '1fr 1fr',
                        notebook: '1fr 1fr',
                        tablet: '1fr',
                    }}
                    styles={{ marginLeft: 0, gap: '40px' }}
                >
                    <StyledGlobal.ContentColumn>
                        <p style={{ textAlign: 'justify', fontSize: '18px', lineHeight: '1.2' }}>
                            O Instituto Ayrton Senna tem como missão contribuir para que cada criança e jovem brasileiro desenvolva plenamente
                            seu potencial por meio de uma educação pública de qualidade. Essa vocação nasce da persistente lacuna na educação
                            básica brasileira que, apesar dos avanços históricos na universalização do acesso à escola desde a Constituição de
                            1988 — atingindo 99,5% de matrículas entre crianças de 6 a 14 anos¹ —, ainda não garante o pleno desenvolvimento dos
                            alunos.
                        </p>
                        <h1>Os desafios educacionais no Brasil são expressivos.</h1>
                        <ResponsiveGrid
                            templates={{
                                notebook: '1fr 1fr',
                                tablet: '1fr',
                            }}
                            styles={{ margin: 0, gap: '40px' }}
                        >
                            <Image
                                width={'50%'}
                                src={fetchStorageFile('anexo_storage_files_5709544379802440400.png_2025-11-19-1763554712253.png')}
                                alt={`Ilustração com dez figuras de crianças alinhadas. As quatro primeiras estão destacadas em azul e as demais seis aparecem em cinza. Abaixo, o texto diz:
                                     ‘Quatro entre dez crianças não estão alfabetizadas ao final do 2º ano do Ensino Fundamental.`}
                                preview={{
                                    style: {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    },
                                }}
                            />
                            <Image
                                width={'50%'}
                                src={fetchStorageFile('anexo_storage_files_1949127538997491720.png_2025-11-19-1763554847567.png')}
                                alt={`Semicírculo composto por segmentos coloridos, sendo cerca da metade em azul e o restante em cinza. O gráfico destaca que 55% dos alunos do 5º ano atingem o desempenho esperado em Língua Portuguesa `}
                                preview={{
                                    style: {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    },
                                }}
                            />
                        </ResponsiveGrid>
                        <div>
                            <Image
                                width={'70%'}
                                src={fetchStorageFile('anexo_storage_files_10955575356524350070.png_2025-11-19-1763554890028.png')}
                                alt={`Texto acompanhado de uma seta apontando para baixo. A mensagem informa que o desempenho dos estudantes diminui ao longo da trajetória escolar, caindo para 36% no 9º ano e para 32% no 3º ano do Ensino Médio. `}
                                preview={{
                                    style: {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    },
                                }}
                            />
                        </div>
                    </StyledGlobal.ContentColumn>
                    <StyledGlobal.ContentColumn>
                        <p style={{ textAlign: 'justify', fontSize: '18px', lineHeight: '1.2' }}>
                            Esses resultados têm consequências diretas sobre os índices de <strong>reprovação e abandono escolar </strong>,
                            gerando atrasos que se acumulam progressivamente. Os dados do INEP ilustram bem esse cenário: a taxa de distorção
                            idade-série — que indica um descompasso mínimo de dois anos entre a idade do estudante e a série adequada — é de
                            <strong> 8% nos anos iniciais</strong> , sobe para <strong> 18% nos anos finais</strong> e chega a{' '}
                            <strong>
                                20% no Ensino Médio <sup style={{ fontSize: 14 }}>4</sup>
                            </strong>{' '}
                            .
                        </p>
                        <Image
                            width={'70%'}
                            src={fetchStorageFile('anexo_storage_files_10627688784859858968.png_2025-11-19-1763556553715.png')}
                            alt={`Texto informativo em azul destacando que a taxa de distorção idade-série aumenta ao longo dos anos: 8% nos anos iniciais, 18% nos anos finais e 20% no Ensino Médio.`}
                            preview={{
                                style: {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                },
                            }}
                        />
                        <p style={{ textAlign: 'justify', fontSize: '18px', lineHeight: '1.2' }}>
                            Esses números revelam o impacto das lacunas de aprendizagem desde as etapas iniciais e a dificuldade do sistema
                            educacional em corrigi-las ao longo do tempo.
                        </p>
                        <Divider />
                        <p style={{ lineHeight: '18px' }}>
                            <sup style={{ fontSize: '10px' }}>1</sup> IBGE (2025). Pesquisa Nacional por Amostra de Domicílios Contínua:
                            Educação. Disponível em: <br />
                            <a href="https://biblioteca.ibge.gov.br/index.php/biblioteca-catalogo?view=detalhes&id=2102180">
                                https://biblioteca.ibge.gov.br/index.php/biblioteca-catalogo?view=detalhes&id=2102180
                            </a>{' '}
                            <br /> <sup style={{ fontSize: '10px' }}>2</sup> MEC - Indicador Criança Alfabetizada (2024). Rede Pública. <br />{' '}
                            <sup style={{ fontSize: '10px' }}>3</sup> MEC/Inep - Saeb (2023). Rede Pública. <br />{' '}
                            <sup style={{ fontSize: '10px' }}>4</sup> MEC/Inep - Censo Escolar (2024). Rede Pública.
                        </p>
                    </StyledGlobal.ContentColumn>
                </ResponsiveGrid>
            </PageTemplatePlanoAnual>
            <PageTemplatePlanoAnual numberPage={6}>
      

                <ResponsiveGrid
                    templates={{
                        desktop: '1fr 1fr',
                        notebook: '1fr 1fr',
                        tablet: '1fr',
                    }}
                    styles={{ marginLeft: 0, gap: '40px' }}
                >
                    <StyledGlobal.ContentColumn>
                        <Image
                            width={'100%'}
                            src={fetchStorageFile('anexo_storage_files_4461110924353498557.webp_2025-11-19-1763559042678.webp')}
                            alt=""
                        />
                    </StyledGlobal.ContentColumn>
                    <StyledGlobal.ContentColumn>
                        <p style={{ textAlign: 'justify', fontSize: '18px', lineHeight: '1.2' }}>
                            Para enfrentar essa realidade, o Instituto Ayrton Senna, em conjunto com as redes públicas de ensino, desenvolve e
                            implementa soluções que promovem o acesso, permanência e desenvolvimento integral dos estudantes. Com o{' '}
                            <strong>
                                firme propósito de utilizar a cultura como instrumento essencial para o desenvolvimento pleno dos estudantes
                            </strong>
                            , o Instituto Ayrton Senna disponibiliza materiais culturais de alta qualidade e fomenta a leitura e a produção
                            criativa, visando a melhoria dos processos de alfabetização e de aprendizado, e a ampliação da criatividade, do senso
                            crítico e da autonomia de crianças e jovens. <br /> <br /> Como forma de alcançar esse objetivo, a partir de 2025, o
                            Instituto Ayrton Senna deu início à implementação do{' '}
                            <strong style={{ color: '#0e61e7' }}>Plano Anual Instituto Ayrton Senna</strong>. Esse plano tem a missão de apoiar
                            municípios de diferentes regiões do país, com profundas defasagens educacionais, levando soluções educacionais e
                            culturais, a fim de ampliar o acesso e a consciência de crianças, jovens e suas famílias sobre o vasto patrimônio
                            cultural brasileiro. Durante a realização das ações, o Instituto convida técnicos, diretores, coordenadores,
                            professores e alunos da rede pública de ensino a se engajarem ativamente na difusão e fomento da leitura.
                        </p>
                    </StyledGlobal.ContentColumn>
                </ResponsiveGrid>
            </PageTemplatePlanoAnual>
            <PageTemplatePlanoAnual numberPage={7}>
                <StyledGlobal.Title blue fontSize="32px">
                    OS 4 PILARES DO PROJETO
                </StyledGlobal.Title>

                <ResponsiveGrid
                    templates={{
                        desktop: '1fr 1fr',
                        notebook: '1fr 1fr',
                        tablet: '1fr',
                    }}
                    styles={{ marginLeft: 0, gap: '40px' }}
                >
                    <StyledGlobal.ContentColumn>
                        <CardItem
                            title="ALFABETIZAÇÃO"
                            subTitle="Circuito 360"
                            imgIcon={fetchStorageFile('anexo_storage_files_1580765269976356996.png_2025-11-19-1763560696422.png')}
                            description="Momento crucial da escolarização, em que as crianças aprendem a ler, escrever e calcular"
                        />
                        <CardItem
                            title="GESTÃO ESCOLA"
                            subTitle="Gestão Nota 10"
                            imgIcon={fetchStorageFile('anexo_storage_files_6461836727386253203.png_2025-11-19-1763560802997.png')}
                            description="Envolve a gestão de todos os aspectos das redes, como a parte pedagógica, financeira,
                            de gestão de pessoas, infraestrutura, entre outras"
                        />
                        <CardItem
                            title="CORREÇÃO DE FLUXO"
                            subTitle="Se Liga e Acelera Brasil"
                            imgIcon={fetchStorageFile('anexo_storage_files_10990361772967721570.png_2025-11-19-1763560938084.png')}
                            description="Tem como objetivo corrigir o atraso na aprendizagem e distorção idade-série de alunos
                            com idade avançada para a série que estão matriculados"
                        />
                        <CardItem
                            title="RECOMPOSIÇÃO DE APRENDIZAGEM"
                            subTitle="Fórmula da Vitória"
                            imgIcon={fetchStorageFile('anexo_storage_files_7326548005118138549.png_2025-11-19-1763560967345.png')}
                            description="Qualificar a aprendizagem dos estudantes em Língua Portuguesa para reversão do quadro
                            de reprovação e exclusão no cotidiano escolar"
                        />
                    </StyledGlobal.ContentColumn>
                    <StyledGlobal.ContentColumn>
                        <img
                            height={'600px'}
                            style={{ objectFit: 'cover', objectPosition: 'top' }}
                            src={fetchStorageFile('anexo_storage_files_9776300314572324956.webp_2025-11-19-1763559208424.webp')}
                            alt=""
                        />
                    </StyledGlobal.ContentColumn>
                </ResponsiveGrid>
            </PageTemplatePlanoAnual>
            <PageTemplatePlanoAnual numberPage={8}>
                <StyledGlobal.Title blue fontSize="32px">
                    OBJETIVOS GERAIS DO PROGRAMA
                </StyledGlobal.Title>

                <ResponsiveGrid
                    templates={{
                        desktop: '1fr 1fr 1fr 1fr 1fr 1fr',
                        notebook: '1fr 1fr 1fr',
                        tablet: '1fr 1fr',
                        mobile: '1fr',
                    }}
                    styles={{ marginLeft: 0, gap: '20px' }}
                >
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_13928301987471528821.png_2025-11-21-1763749836696.png')}
                        description={`
                            Ampliar as
                            oportunidades de
                            desenvolvimento
                            pleno, alfabetização
                            e leitura de qualidade
                            para crianças e
                            jovens
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_15375913586171659363.png_2025-11-21-1763749880259.png')}
                        description={`
                                Fortalecer a
                                formação e
                                desenvolvimento
                                de diretores,
                                coordenadores
                                pedagógicos e
                                professores
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_4552631191875550692.png_2025-11-21-1763749900357.png')}
                        description={`
                            Disponibilizar
                            materiais de
                            literatura para
                            alunos com
                            defasagem de idade
                            ou aprendizagem
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_1167037294205844524.png_2025-11-21-1763749921820.png')}
                        description={`
                            Orientar as
                            equipes para que
                            a literatura seja
                            disponibilizada em
                            sala de aula
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_5840563438383730174.png_2025-11-21-1763749949712.png')}
                        description={`
                            Disponibilizar para
                            os educadores
                            modelos de projetos
                            que tenham a
                            arte como apoio
                            para o ensino e a
                            aprendizagem
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                    <CardItem
                        imgIcon={fetchStorageFile('anexo_storage_files_9498463553859835734.png_2025-11-21-1763749963740.png')}
                        description={`
                            Despertar nos
                            estudantes a
                            curiosidade, o
                            interesse e o senso
                            crítico pela leitura e
                            pelo audiovisual
                            `}
                        isColumn
                        imgStyle={{ width: '80px', height: '100px' }}
                    />
                </ResponsiveGrid>
            </PageTemplatePlanoAnual>
        </>
    );
};

export default SessionGeralPlanoAnual;
